import { zodResolver } from "@hookform/resolvers/zod";
import { ReactNode, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";

import { Button } from "atoms/button/Button";
import { TextField } from "atoms/text-field/TextField";

import OnboardingHeaderV3 from "../../components/OnboardingScreenHeader/OnboardingHeaderV3";

// Zod schema for name validation
const nameFormSchema = z.object({
  firstName: z
    .string()
    .min(1, "First name is required")
    .min(2, "First name must be at least 2 characters")
    .max(50, "First name must be less than 50 characters")
    .regex(
      /^[a-zA-Z\s\-']+$/,
      "First name can only contain letters, spaces, hyphens, and apostrophes"
    ),
  lastName: z
    .string()
    .min(1, "Last name is required")
    .min(2, "Last name must be at least 2 characters")
    .max(50, "Last name must be less than 50 characters")
    .regex(
      /^[a-zA-Z\s\-']+$/,
      "Last name can only contain letters, spaces, hyphens, and apostrophes"
    ),
});

export type NameFormData = z.infer<typeof nameFormSchema>;

export interface NameFormProps {
  initialFirstName?: string;
  initialLastName?: string;
  onSubmit?: (data: NameFormData) => void | Promise<void>;
  className?: string;
  submitButtonText?: string;
  prefix?: ReactNode;
  suffix?: ReactNode;
}

export const NameForm = ({
  initialFirstName = "",
  initialLastName = "",
  onSubmit,
  className = "",
  submitButtonText = "Continue",
  prefix,
  suffix,
}: NameFormProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | undefined>(undefined);

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<NameFormData>({
    resolver: zodResolver(nameFormSchema),
    defaultValues: {
      firstName: initialFirstName,
      lastName: initialLastName,
    },
    // only show errors after submit, so we dont see red when typing our name
    mode: "onSubmit",
  });

  const onSubmitForm = async (data: NameFormData) => {
    if (!onSubmit) return;

    setIsSubmitting(true);
    setSubmitError(undefined);

    try {
      await onSubmit(data);
    } catch (error) {
      setSubmitError(
        error instanceof Error
          ? error.message
          : "An error occurred while saving your information. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" && isValid && !isSubmitting) {
      event.preventDefault();
      handleSubmit(onSubmitForm)();
    }
  };

  return (
    <div className={`flex w-full max-w-sm flex-col gap-9 ${className}`}>
      {/* Optional prefix slot for logo */}
      {prefix ? (
        <div className="flex w-full items-center justify-center">{prefix}</div>
      ) : null}

      {/* Header */}
      <OnboardingHeaderV3 title="What's your name?" align="left" />

      <form
        onSubmit={handleSubmit(onSubmitForm)}
        className="flex flex-col gap-3"
      >
        {/* First Name Field */}
        <Controller
          name="firstName"
          control={control}
          render={({ field }) => (
            <TextField
              type="text"
              label="First name"
              placeholder="First name"
              name="firstName"
              autoComplete="given-name"
              autoFocus
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              invalid={!!errors.firstName}
              supportingText={errors.firstName?.message}
              disabled={isSubmitting}
              onKeyDown={handleKeyDown}
              labelClassName="hidden"
              className="!ring-0 focus:!ring-0"
            />
          )}
        />

        {/* Last Name Field */}
        <Controller
          name="lastName"
          control={control}
          render={({ field }) => (
            <TextField
              type="text"
              label="Last name"
              placeholder="Last name"
              name="lastName"
              autoComplete="family-name"
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              invalid={!!errors.lastName}
              supportingText={errors.lastName?.message}
              disabled={isSubmitting}
              onKeyDown={handleKeyDown}
              labelClassName="hidden"
              className="!ring-0 focus:!ring-0"
            />
          )}
        />

        {/* Submit Button with Error */}
        <div className="mt-6 flex flex-col gap-2">
          {submitError && (
            <div className="text-center text-sm text-red">{submitError}</div>
          )}
          <Button submit fullWidth disabled={!isValid || isSubmitting}>
            {isSubmitting ? "Saving..." : submitButtonText}
          </Button>
        </div>
      </form>

      {/* Optional suffix slot for extra content */}
      {suffix ? <div className="mt-6 w-full">{suffix}</div> : null}
    </div>
  );
};
